import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  DocumentReference,
  QueryDocumentSnapshot,
} from "@atgof-firebase/firebase";
import React from "react";
import { RootStackParamList } from "../types";
import CollectionList, {
  CollectionListSpec,
} from "../components/CollectionList";
import {
  Scene,
  compareScenes,
  scenesCollection,
  scenesQuery,
  sortScenes,
  toScene,
} from "../common/scene";
import { Platform, ScrollView, useWindowDimensions, View } from "react-native";
import { TabBar, TabView } from "react-native-tab-view";
import NavPage, { useEditor } from "../components/pages/NavPage";
import { BreakdownView } from "../components/BreakdownView";
import { LanguageContext } from "../common/language";
import { ScriptButton } from "../components/ScriptButton";
import { ProgressBar, useTheme } from "react-native-paper";
import { toEpisode } from "../common/episode";
import ListItem from "../components/ListItem";
import SceneInfo from "../components/SceneInfo";
import { useNavigation } from "@react-navigation/native";
import { formatDate } from "../common/util";
import { ProjectContext } from "../data/projectContext";
import { EpisodeTimingsView } from "../components/EpisodeTimingsView";
import { useDocumentReference } from "../data/firestore";
import { useDocument } from "../data/useDocument";
import { Timeline } from "../components/Timeline";
import { useDocuments } from "../data/useDocuments";
import { EpisodeInfo } from "../components/EpisodeInfo";
import { useBreakdownTablePrinter } from "../components/BreakdownTable";
import { notDeleted } from "@atgof-common/general";

function TimelineView({
  episodeRef,
}: {
  episodeRef: DocumentReference | undefined;
}) {
  const sceneDocs = useDocuments(scenesQuery(episodeRef), [episodeRef?.path]);
  const scenes = React.useMemo(
    () => sortScenes((sceneDocs ?? []).map((snapshot) => toScene(snapshot))),
    [sceneDocs],
  );
  const cast = React.useMemo<string[]>(
    () => [...new Set((scenes ?? []).flatMap((scene) => scene.cast ?? []))],
    [scenes],
  );
  return (
    <ScrollView>
      <ScrollView horizontal>
        <Timeline scenes={scenes} cast={cast ?? []} />
      </ScrollView>
    </ScrollView>
  );
}

function ScenesView({
  episodeRef,
}: {
  episodeRef: DocumentReference | undefined;
}) {
  const navigation = useNavigation();
  const [collections, setCollections] = React.useState<
    CollectionListSpec<Scene, void>[] | undefined
  >();
  React.useEffect(() => {
    setCollections(
      episodeRef
        ? [
            {
              collection: scenesCollection(episodeRef)!,
              addConstraints: notDeleted,
              sortComparison: compareScenes,
              process: (snapshots: QueryDocumentSnapshot[]) =>
                snapshots.map((snapshot) => toScene(snapshot)),
              renderItem: ({ item }) => (
                <ListItem
                  onPress={() =>
                    navigation.navigate("scene", { scenePath: item.ref.path })
                  }
                >
                  <SceneInfo
                    style={{ marginTop: 8, marginBottom: 8 }}
                    scene={item}
                  />
                </ListItem>
              ),
              searchTokens: (item) => [
                item.id,
                item.episodeId,
                ...(item.cast ?? []),
                item.comment,
                formatDate(item.filmingDate, true),
                item.location,
                ...(item.sets ?? []),
                item.synopsis,
              ],
            },
          ]
        : undefined,
    );
  }, [episodeRef, navigation]);
  return <CollectionList collections={collections} estimatedItemSize={100} />;
}

function renderTabBar(props: any) {
  const { colors } = useTheme();
  return (
    <TabBar
      {...props}
      indicatorStyle={{ backgroundColor: colors.primary }}
      style={{
        backgroundColor: colors.secondaryContainer,
        color: colors.onSecondaryContainer,
      }}
      activeColor={colors.onSecondaryContainer}
      inactiveColor={colors.onSecondaryContainer}
    />
  );
}

function Tabs({
  episodeRef,
  setPrintButton,
}: {
  episodeRef: DocumentReference | undefined;
  setPrintButton: (printButton: React.ReactElement | null) => void;
}) {
  const layout = useWindowDimensions();
  const { ph } = React.useContext(LanguageContext);
  const { categories } = React.useContext(ProjectContext);
  const { breakdownTableRef, printButton } = useBreakdownTablePrinter();
  const [index, setIndex] = React.useState(0);
  const routes = React.useMemo(
    () => [
      { key: "scenes", title: ph("scenes") as string },
      ...(categories.includes("pa")
        ? [{ key: "timings", title: ph("timings") as string }]
        : []),
      { key: "breakdown", title: ph("breakdown") as string },
      { key: "timeline", title: ph("timeline") as string },
    ],
    [ph, categories],
  );
  const isBreakdownSelected = routes[index].key === "breakdown";
  React.useEffect(
    () => setPrintButton(isBreakdownSelected ? printButton : null),
    [isBreakdownSelected, printButton],
  );
  const renderScene = React.useCallback(
    ({ route }: { route: { key: string } }) => {
      switch (route.key) {
        case "scenes":
          return <ScenesView episodeRef={episodeRef} />;
        case "timings":
          return <EpisodeTimingsView episodeRef={episodeRef} />;
        case "breakdown":
          return (
            <BreakdownView ref={breakdownTableRef} episodeRef={episodeRef} />
          ); // TODO
        case "timeline":
          return <TimelineView episodeRef={episodeRef} />;
      }
    },
    [episodeRef?.path],
  );
  return (
    <TabView
      lazy
      navigationState={{ index, routes }}
      renderTabBar={renderTabBar}
      renderScene={renderScene}
      onIndexChange={setIndex}
      initialLayout={{ width: layout.width }}
    />
  );
}

export default function EpisodeScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "episode">) {
  const { episodePath } = route.params;
  const { categories } = React.useContext(ProjectContext);
  const [printButton, setPrintButton] =
    React.useState<React.ReactElement | null>();
  const episodeRef = useDocumentReference(episodePath);
  const episodeDoc = useDocument(episodeRef);
  const episode = React.useMemo(
    () => (episodeDoc ? toEpisode(episodeDoc) : undefined),
    [episodeDoc],
  );
  const { editor, editingMode } = useEditor(true, "done");
  const headerRight = React.useMemo(
    () => (
      <View style={{ flexDirection: "row" }}>
        {printButton}
        <ScriptButton wholeEpisode parent={episode} />
      </View>
    ),
    [episode, printButton],
  );
  return (
    <NavPage
      fullScreen
      newSheetContext={{ episodeRef }}
      headerRight={headerRight}
      editor={
        categories.includes("pa") ||
        categories.includes("admin") ||
        categories.includes("postProduction")
          ? editor
          : undefined
      }
    >
      {episode ? (
        <View>
          <EpisodeInfo episode={episode} readonly={!editingMode} />
        </View>
      ) : (
        <ProgressBar indeterminate />
      )}
      {Platform.OS === "web" ? (
        <Tabs episodeRef={episodeRef} setPrintButton={setPrintButton} />
      ) : (
        <ScenesView episodeRef={episodeRef} />
      )}
    </NavPage>
  );
}
